import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check } from '../skillUsage';

export const momentSweepTransitionSkill: CodeoSkill = {
    name: SKILL_NAMES.momentSweepTransition,
    kind: 'codeo',
    description:
        'Wipe from one on-screen state to another within a scene with a soft full-canvas light sweep ' +
        'or clip-path wipe, so the look changes instead of jump-cutting — use at content beats that would otherwise feel like a slideshow cut.',
    documentUsesSkill: check(
        (script, markup) =>
            /linear-gradient/.test(markup) && /xPercent|\bx:\s*-?\d{2,}/.test(script),
    ),
    instructions: `# Moment Sweep Transition

A translucent band of light sweeps across the canvas exactly at a beat where the scene's content changes look — a headline swaps for a different one, a background treatment shifts — with the outgoing content exiting under it and the incoming content revealed behind it. The sweep gives the eye a single continuous motion to follow through the handoff — the difference between "the content changed" and "the slide changed". This is motion within ONE scene: both the before and after states already live in the same graphic's DOM the whole time; the sweep just crosses over them at the right instant.

## Markup

One persistent overlay, holding the band, sits above both content states so it can wipe over either of them. Both text states share a grid cell so they land at the same spot; the "after" line starts at \`opacity: 0\` in CSS so it isn't visible until the swap.

\`\`\`html
<div id="stage" style="position: absolute; inset: 0; background: var(--l5-color-2, #f4f4f8);">
  <div style="position: absolute; inset: 0; display: grid; place-items: center;">
    <h1 id="line-before"
        style="grid-area: 1/1; margin: 0; text-align: center; font-family: var(--l5-font-0), sans-serif; font-size: 96px; color: var(--l5-color-0, #101820);">The old way</h1>
    <h1 id="line-after"
        style="grid-area: 1/1; margin: 0; text-align: center; opacity: 0; font-family: var(--l5-font-0), sans-serif; font-size: 96px; color: var(--l5-color-0, #101820);">The new way</h1>
  </div>
  <div id="sweep-layer" style="position: absolute; inset: 0; pointer-events: none; overflow: hidden;">
    <div id="sweep-band" data-l5-allow-offscreen
         style="position: absolute; top: 0; bottom: 0; left: 0; width: 36%; opacity: 0;
                background: linear-gradient(90deg, transparent, var(--l5-color-2, #ffffff), transparent);"></div>
  </div>
</div>
\`\`\`

The band waits off one edge, crosses the frame and leaves by the other, so it is outside the canvas for most of the scene — \`data-l5-allow-offscreen\` says that travel is the design. Its \`overflow: hidden\` parent is what keeps the band from painting outside the frame; the mark is about the band's position, not its paint.

## Timeline code

The band's motion is split into two legs — fade-in-while-entering, then fade-out-while-exiting — so both legs' own from-states are \`opacity: 0\`. That keeps default \`immediateRender\` correct with no flash, so neither leg needs \`immediateRender: false\`. The content swap lands right as the band is fully over the frame.

\`\`\`js
const tl = gsap.timeline({ paused: true });

function sweepAt(t) {
    tl.fromTo('#sweep-band',
        { xPercent: -110, opacity: 0 },
        { xPercent: -20, opacity: 0.55, duration: 0.3, ease: 'power2.in' },
        t - 0.4);
    tl.to('#sweep-band', { xPercent: 320, opacity: 0, duration: 0.5, ease: 'power2.out' }, t - 0.1);
    tl.set('#line-before', { opacity: 0 }, t);
    tl.set('#line-after', { opacity: 1 }, t);
}

sweepAt(2.0);  // the beat between the "before" state and the "after" state
window.__timelines = { main: tl };
\`\`\`

Time the outgoing state's exit to finish as the band crosses its position, and the incoming state's reveal to land just behind the band.

## Adapt

- Direction: sweep left with \`xPercent\` reversed; vertical with \`yPercent\` and a horizontal gradient rotated to \`180deg\`. Keep ONE direction per scene — the sweep is part of the scene's motion language.
- Color: \`var(--l5-color-2, ...)\` reads as light passing; an accent-colored band is louder and suits energetic styles.
- Clip-path wipe variant for a harder hand-off: a full-canvas panel with \`tl.fromTo(panel, { clipPath: 'inset(0 100% 0 0)' }, { clipPath: 'inset(0 0 0 0)', ... })\`, then wipe out the other side. Panels are flat fills — no full-screen dark linear gradients (they band under H.264).
- Don't sweep every content change mechanically. Sweep the pivots (problem→solution, setup→payoff) within the scene; let small consecutive beats flow without one.
- Multiple beats in the same scene: call \`sweepAt\` again at a later time with a third content state layered into the same grid cell.

## Pitfalls

- The band's own from-state is \`opacity: 0\` on both legs (entering and exiting), so default \`immediateRender\` already keeps it hidden until each leg runs — don't add \`immediateRender: false\`, it isn't needed and the contract flags it as wrong when the from-state is already hidden.
- \`pointer-events: none\` on the sweep layer, or it blocks the user from clicking assets under it in the preview.
- Keep the band under ~40% canvas width; a wider band reads as a flash, not a sweep.
- The "after" text's static CSS \`opacity: 0\` and the \`tl.set\` that reveals it don't touch a transform channel, so there's nothing to conflict with any CSS \`transform\` — but don't add one to \`#line-before\`/\`#line-after\` regardless, since \`opacity\` swaps are all this needs.`,
};
